import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, gbp } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { HotelCard } from "@/components/HotelCard";
import { BookingDialog } from "@/components/BookingDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Star, Zap, Info } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export default function Hotels() {
  const [params] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ cityCode: "DXB", checkIn: plusDays(30), checkOut: plusDays(33), adults: 2 });
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    setLoading(true);
    const q = {};
    const city = params.get("city") || params.get("destination");
    if (city) q.city = city;
    api.get("/hotels", { params: q }).then(({ data }) => setHotels(data)).finally(() => setLoading(false));
  }, [params]);

  const searchLive = async (e) => {
    e.preventDefault();
    setLiveLoading(true);
    try {
      const { data } = await api.get("/hotels/live", {
        params: { cityCode: form.cityCode.toUpperCase(), checkIn: form.checkIn, checkOut: form.checkOut, adults: Number(form.adults) || 2 },
      });
      setLive(data);
    } catch {
      setLive({ source: "fallback", live: false, data: [], message: "Could not load availability. Please try again." });
    } finally {
      setLiveLoading(false);
    }
  };

  return (
    <div>
      <PageHero title="Find your hotel" subtitle="Handpicked stays worldwide — from city breaks to Haram-view suites." image="https://images.pexels.com/photos/97083/pexels-photo-97083.jpeg" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">

        {/* Live availability search */}
        <form onSubmit={searchLive} data-testid="hotel-live-search"
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5 grid gap-3 md:grid-cols-[1fr_1fr_1fr_0.8fr_auto] md:items-end mb-8">
          <div>
            <Label className="text-xs text-slate-500">City code</Label>
            <Input data-testid="hotel-city-input" value={form.cityCode} maxLength={3}
              onChange={(e) => set("cityCode", e.target.value.toUpperCase())} placeholder="DXB" className="uppercase" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Check-in</Label>
            <Input data-testid="hotel-checkin" type="date" min={today()} value={form.checkIn} onChange={(e) => set("checkIn", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Check-out</Label>
            <Input data-testid="hotel-checkout" type="date" min={form.checkIn} value={form.checkOut} onChange={(e) => set("checkOut", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Guests</Label>
            <Input data-testid="hotel-adults" type="number" min="1" value={form.adults} onChange={(e) => set("adults", e.target.value)} />
          </div>
          <Button data-testid="hotel-search-btn" type="submit" disabled={liveLoading}
            className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-6 py-6">
            {liveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Search</>}
          </Button>
        </form>

        {/* Live results */}
        {live && (
          <div className="mb-12" data-testid="hotel-live-results">
            <div className="flex items-center gap-2 mb-4">
              {live.live ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00b67a]"><Zap className="w-3.5 h-3.5" /> Live availability · {form.cityCode.toUpperCase()}</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-amber-600"><Info className="w-3.5 h-3.5" /> {live.message || "Indicative curated rates"}</span>
              )}
            </div>
            {live.data.length === 0 ? (
              <p className="text-slate-500 text-sm">No hotels found for those dates.</p>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {live.data.map((h, i) => (
                  <div key={h.hotelId || i} data-testid={`live-hotel-${i}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-1 text-[#FF6B35] mb-1">
                      {Array.from({ length: h.stars || 4 }).map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-[#FF6B35]" />)}
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 leading-tight">{h.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{h.city || form.cityCode.toUpperCase()}</p>
                    <div className="mt-auto pt-4 flex items-end justify-between">
                      <div>
                        <p className="text-[11px] text-slate-400">from</p>
                        <p className="font-display text-xl font-bold text-[#0B3C5D]">{gbp(h.price)}<span className="text-xs font-normal text-slate-400"> /night</span></p>
                      </div>
                      <BookingDialog productType="hotel"
                        item={{ id: h.hotelId, name: h.name, city: h.city, image: h.image, price: h.price }}
                        unitPrice={h.price}
                        trigger={<Button data-testid={`book-live-hotel-${i}`} className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold" size="sm">Book</Button>} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Curated catalog */}
        <p className="text-slate-600 text-sm mb-6" data-testid="hotels-count">{loading ? "Searching…" : `${hotels.length} featured stays`}</p>
        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B3C5D]" /></div> : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">{hotels.map((h) => <HotelCard key={h.id} h={h} />)}</div>
        )}
        {!loading && hotels.length === 0 && <p className="text-center text-slate-500 py-20">No hotels found for that city.</p>}
      </div>
    </div>
  );
}
